import { cn } from "@/lib/utils";
import { differenceInMinutes, format, parseISO } from "date-fns";
import { Calendar, MapPin, PlusSquare, Users, Video } from "lucide-react";
import { useState } from "react";
import { Button } from "../../ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../ui/tooltip";
import {
  BaseEntityCard,
  BaseEntityCardActions,
  BaseEntityCardBody,
  BaseEntityCardContent,
  BaseEntityCardExternalLinkAction,
  BaseEntityCardHeader,
  BaseEntityCardIcon,
  BaseEntityCardMentionAction,
  BaseEntityCardSkeleton,
  BaseEntityCardTitle,
  ContextMenuAction,
  Process,
} from "../BaseCard";

import type { Meeting } from "@cohesive/api-entities";

type MeetingCardProps = Meeting & {
  onClick?: () => void;
  onDoubleClick?: () => void;
  onMentionClick?: () => void;
  platform: string;
  actions?: ContextMenuAction[];
  processes?: Process[];
  className?: string;
  style?: React.CSSProperties;
  showJoinButton?: boolean;
};

// Avatar component for MeetingCard
// Note: This component accepts extra props for compatibility with EntityCard avatar rendering
export function MeetingCardAvatar({
  platform,
  className,
  style,
}: Partial<MeetingCardProps> & {
  className?: string;
  style?: React.CSSProperties;
}) {
  const [iconError, setIconError] = useState(false);

  // For unknown, missing platform, or failed icon load, show generic Video icon with grey background
  if (!platform || platform === "unknown" || iconError) {
    return (
      <BaseEntityCardIcon className={className} style={style}>
        <Video className="h-5 w-5 text-muted-foreground" />
      </BaseEntityCardIcon>
    );
  }

  // Normalize platform name: convert spaces and underscores to hyphens
  const normalizedPlatform = platform.toLowerCase().replace(/[\s_]/g, "-");

  // Show platform icon without grey background (like DealCard)
  return (
    <BaseEntityCardIcon
      className={cn("bg-transparent", className)}
      style={style}
    >
      <img
        src={`${import.meta.env.VITE_STATIC_BASE_URL || ""}/images/integrations/logos/${normalizedPlatform}.svg`}
        alt={platform}
        className="h-full w-full"
        onError={() => setIconError(true)}
      />
    </BaseEntityCardIcon>
  );
}

export function MeetingCard({
  platform,
  title,
  body = "",
  start_time,
  end_time,
  timestamp,
  location,
  web_link,
  attendees,
  join_url,
  onClick,
  onDoubleClick,
  onMentionClick,
  actions,
  processes,
  className,
  style,
  showJoinButton = true,
}: MeetingCardProps) {
  // Format date as "MMM d, h:mm a" (e.g., "Jan 20, 3:00 PM") to match CalendarEventCard
  const formattedMeetingDate = start_time
    ? format(parseISO(start_time), "MMM d, h:mm a")
    : timestamp
      ? format(parseISO(timestamp), "MMM d, h:mm a")
      : "";

  const bodyPreview =
    body && body.length > 100 ? `${body.substring(0, 100)}...` : body;

  // Check if meeting is in the future
  const isFutureMeeting = start_time && new Date(start_time) > new Date();

  // Calculate meeting duration
  const getMeetingDuration = () => {
    if (!start_time || !end_time) return "";

    try {
      const startDate = parseISO(start_time);
      const endDate = parseISO(end_time);
      const minutes = differenceInMinutes(endDate, startDate);

      if (minutes < 60) {
        return `${minutes}m`;
      } else {
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;
        return remainingMinutes > 0
          ? `${hours}h ${remainingMinutes}m`
          : `${hours}h`;
      }
    } catch (error) {
      return "";
    }
  };

  const duration = getMeetingDuration();

  return (
    <BaseEntityCard
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      actions={actions}
      processes={processes}
      className={className}
      style={style}
    >
      <MeetingCardAvatar platform={platform} />
      <BaseEntityCardContent>
        <BaseEntityCardHeader>
          <BaseEntityCardTitle>{title}</BaseEntityCardTitle>
          <BaseEntityCardActions>
            {showJoinButton && join_url && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      asChild
                    >
                      <a
                        href={join_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <PlusSquare className="h-4 w-4" />
                      </a>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Join meeting</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {web_link && <BaseEntityCardExternalLinkAction href={web_link} />}
            {onMentionClick && (
              <BaseEntityCardMentionAction onClick={onMentionClick} />
            )}
          </BaseEntityCardActions>
        </BaseEntityCardHeader>
        <BaseEntityCardBody>
          {bodyPreview && (
            <div className="text-sm text-foreground line-clamp-2">
              {bodyPreview}
            </div>
          )}

          {/* Date & Duration */}
          {(formattedMeetingDate || duration) && (
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <Calendar className="h-3.5 w-3.5" />
              <span>
                {formattedMeetingDate &&
                  (isFutureMeeting
                    ? `Starts ${formattedMeetingDate}`
                    : formattedMeetingDate)}
                {formattedMeetingDate && duration && (
                  <>
                    <span className="mx-1">•</span>
                    {duration}
                  </>
                )}
              </span>
            </div>
          )}

          {/* Outcome, Attendees & Location */}
          <div className="flex flex-wrap gap-x-4 gap-y-2 items-center">
            {attendees && attendees.length > 0 && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Users className="h-3 w-3" />
                <span>
                  {attendees.length} attendee
                  {attendees.length > 1 ? "s" : ""}
                </span>
              </div>
            )}

            {location && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground overflow-hidden">
                      <MapPin className="h-3 w-3 shrink-0" />
                      <span className="truncate">{location}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{location}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </BaseEntityCardBody>
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}

export function MeetingCardSkeleton({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return <BaseEntityCardSkeleton className={className} style={style} />;
}
